// Card.js

/*
    flow:       // unique identifier for the flow to which the card belongs
    index:      // index of the card within the flow (indicating position from 0: first card to n: nth card)

    component:  // component associated with the card
    category:   // category of the card
    prompt:     // prompt associated with the card (user, system, augmentation, balance, dual, quality)
    assets:     // assets storage of the card (every card should have its own assets)
    primitive:  // whether the card has primitive assets (i.e., assets do not have files)

    skipped:    // whether the card should be skipped (i.e., try-again was used for that card)
    ignored:    // whether the card is ignored (i.e., should not be processed)

    option:     // option selected on the card (if the card has options)
    action:     // action selected on the card (if the card has actions)

    custom:     // any custom properties relevant for the card (e.g., balance, augmentation, transparency, views)

    stage:      // (local) stage of the card (additional mechanism to update cards during long-running blocking actions)

    transaction: // transaction that created this card
*/

import CATEGORY from '@generio-ai/library-legacy/constants/category'
import CARD from "@/constants/card.js"
import VERSION from '@/constants/version.js'
import { Prompt } from "@/classes/prompt.js"

export class Card 
{
    // create a new root card
    static newRoot() 
    {
        return new Card(
            null,
            0, 
            CARD.SELECT_FLOW,
            CATEGORY.DEFAULT,
            new Prompt(), 
            null
        )
    }

    // constructor
    constructor(flow, index, component, category, prompt, transaction=null) 
    {
        // identifying properties
        this.flow = flow
        this.index = index

        // base properties
        this.component = component
        this.category = category || CATEGORY.DEFAULT
        this.prompt = prompt
        this.assets = null
        this.primitive = false
        this.skipped = false

        // selected option and/or action
        this.option = null
        this.action = null

        // custom properties (e.g., balance, augmentation, transparency, views)
        this.custom = {}

        // local state
        this.stage = null

        // transaction to create this card
        this.transaction = transaction 
    }

    // reset the selection
    reset() {
        this.option = null
        this.action = null
    }

    // get all asset ids
    getAssetIds() {
        if (this.assets) return this.assets.map(asset => asset.id)
        return null
    }

    // get the (max. one) selected asset
    getSelectedAsset() {
        if (this.assets && this.assets.length > 0) {
            return this.assets.find(asset => asset.id === this.option)
        }
        return null
    }

    // get selected asset or if not available, all assets
    // returns 1 to n assets
    getSelectedOrAllAssets(limit = null) {
        if (this.assets && this.assets.length > 0) {
            const asset = this.getSelectedAsset()
            if (asset) return [ asset ]
            if (limit && this.assets.length > limit) {
                return this.assets.slice(0, limit)
            }
            return this.assets
        }
        return []
    }

    // get selected asset or if not available, first asset
    // returns 1 asset always 
    getSelectedOrFirstAsset() {
        const assets = this.getSelectedOrAllAssets(1)
        if (assets.length > 0) return assets[0]
        return null
    }

    // get description of the card
    getDescription() {
        switch (this.component) {
            case CARD.IMAGE:
                return `edit ${this.category}`
            case CARD.IMPORT:
                return 'import images'
            case CARD.MODEL:
                return 'view model'
            case CARD.PROMPT:
                return 'edit description'
            case CARD.OPTIMIZE:
                return 'optimize model'
            case CARD.GENERATE_GEOMETRY:
                return 'generate geometry'
            case CARD.GENERATE_TEXTURE:
                return 'generate texture'
            case CARD.SELECT_ASSET:
                return `select ${this.category}`
            case CARD.SELECT_FLOW:
                return 'select flow'
            case CARD.SELECT_TEXTURE:
                return 'select texturing'
            default:
                return 'no description available'
        }
    }

    // create additional for a new asset (belonging to this card)
    createAdditional(parentCard, ignore=false) {
        return {
            "flow": this.flow,
            "card": {
                "index": this.index,
                "component": this.component,
                "option": this.option,
                "action": this.action,
            },
            "category": this.category,
            "prompt": this.prompt?.toJSON(),
            "parents": parentCard?.getAssetIds(),
            "primitive": this.primitive,
            "skipped": this.skipped, // used when user says try again (card is completely skipped)
            "ignore": ignore, // assets created in a flow but irrelevant for the flow (intermediate assets that are only byproducts)
            "rating": 0,
            "custom": this.custom,
            "version": VERSION
        }
    }

    // create additional for a new child card (belonging to a new card)
    createChildAdditional(component, category, ignore=false) {
        return {
            "flow": this.flow,
            "card": {
                "index": this.index + 1,
                "component": component,
                "option": null,
                "action": null,
            },
            "category": category,
            "prompt": this.prompt?.toJSON(),
            "parents": this.getAssetIds(),
            "primitive": false,
            "skipped": false,
            "ignore": ignore, // assets created in a flow but irrelevant for the flow (intermediate assets that are only byproducts)
            "rating": 0,
            "custom": {},
            "version": VERSION
        }
    }
}